import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FaArrowDown,
  FaArrowRight,
  FaArrowUp,
  FaBuilding,
  FaEye,
  FaGavel,
  FaGripVertical,
  FaIndustry,
  FaSave,
  FaShoppingBag,
  FaTh,
  FaTruck,
  FaUserFriends,
} from "react-icons/fa";
import {
  fetchHubHomeSections,
  hubHomeSectionExtractMessage,
  updateHubHomeSection,
} from "../../services/hubHomeSectionAdmin.service";
import {
  HUB_HOME_SECTION_KEYS,
  HUB_MODULE_TILE_OPTIONS,
  resolveVisibleModuleTileKeys,
} from "../../constants/hubHomeSections";
import "../../assets/styles/master_category_admin.scss";
import "../../assets/styles/super_admin.scss";
import "../../assets/styles/super_admin_service_quick_links.scss";

const TILE_ICONS = {
  auctions: FaGavel,
  cm: FaIndustry,
  infra: FaBuilding,
  service_providers: FaUserFriends,
  logistics: FaTruck,
  p2p: FaShoppingBag,
};

function ToggleSwitch({ checked, disabled, onChange, label }) {
  return (
    <label className="sa-toggle" title={label}>
      <input
        type="checkbox"
        className="sa-toggle__input"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        aria-label={label}
      />
      <span className="sa-toggle__track" aria-hidden />
    </label>
  );
}

export default function SuperAdminServiceQuickLinksPage() {
  const [section, setSection] = useState(null);
  const [moduleTileKeys, setModuleTileKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const labelByKey = useMemo(
    () => Object.fromEntries(HUB_MODULE_TILE_OPTIONS.map((option) => [option.key, option.label])),
    [],
  );

  const hiddenTiles = useMemo(
    () => HUB_MODULE_TILE_OPTIONS.filter((option) => !moduleTileKeys.includes(option.key)),
    [moduleTileKeys],
  );

  const orderedTileOptions = useMemo(() => {
    const visible = moduleTileKeys
      .map((key) => HUB_MODULE_TILE_OPTIONS.find((option) => option.key === key))
      .filter(Boolean);
    return [...visible, ...hiddenTiles];
  }, [moduleTileKeys, hiddenTiles]);

  const allVisible = moduleTileKeys.length === HUB_MODULE_TILE_OPTIONS.length;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await fetchHubHomeSections();
      const moduleTilesSection = rows.find(
        (row) => (row.sectionKey || row.section_key) === HUB_HOME_SECTION_KEYS.MODULE_TILES,
      );
      setSection(moduleTilesSection || null);
      setModuleTileKeys(
        resolveVisibleModuleTileKeys(moduleTilesSection?.sectionConfig ?? moduleTilesSection?.section_config),
      );
    } catch (err) {
      toast.error(hubHomeSectionExtractMessage(err, "Could not load Service Quick Links settings."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const toggleTile = (key, checked) => {
    if (checked) {
      setModuleTileKeys((prev) => (prev.includes(key) ? prev : [...prev, key]));
      return;
    }
    if (moduleTileKeys.length <= 1) return;
    setModuleTileKeys((prev) => prev.filter((item) => item !== key));
  };

  const selectAllTiles = (checked) => {
    if (checked) {
      setModuleTileKeys(HUB_MODULE_TILE_OPTIONS.map((option) => option.key));
      return;
    }
    if (moduleTileKeys.length <= 1) return;
    setModuleTileKeys([moduleTileKeys[0]]);
  };

  const moveTile = (index, direction) => {
    const nextIndex = direction === "up" ? index - 1 : index + 1;
    if (nextIndex < 0 || nextIndex >= moduleTileKeys.length) return;
    setModuleTileKeys((prev) => {
      const next = [...prev];
      [next[index], next[nextIndex]] = [next[nextIndex], next[index]];
      return next;
    });
  };

  const saveCards = async () => {
    if (!section) {
      toast.error("Service Quick Links section was not found.");
      return;
    }
    if (!moduleTileKeys.length) {
      toast.error("At least one card must remain visible.");
      return;
    }

    setSaving(true);
    try {
      const updated = await updateHubHomeSection(section.id, {
        section_config: { visible_module_tiles: moduleTileKeys },
      });
      if (updated) {
        setSection(updated);
        setModuleTileKeys(
          resolveVisibleModuleTileKeys(updated.sectionConfig ?? updated.section_config),
        );
        toast.success("Service Quick Links cards updated.");
      }
    } catch (err) {
      toast.error(hubHomeSectionExtractMessage(err, "Could not update Service Quick Links cards."));
      await load();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mca-page super-admin-page sa-sql-page">
      <nav className="mca-breadcrumb" aria-label="Breadcrumb">
        <ol className="mca-breadcrumb__list">
          <li>
            <Link to="/super-admin/dashboard">Super Admin</Link>
          </li>
          <li>
            <Link to="/super-admin/master-data/home-section-management">Home Section Management</Link>
          </li>
          <li aria-current="page">Service Quick Links</li>
        </ol>
      </nav>

      <div className="sa-sql-page-head">
        <header className="sa-sql-hero">
          <div className="sa-sql-hero__title-row">
            <span className="sa-sql-hero__icon" aria-hidden>
              <FaTh />
            </span>
            <h1 className="sa-sql-hero__title">Service Quick Links</h1>
            <span className="sa-sql-hero__badge">Super Admin</span>
          </div>
          <p className="sa-sql-hero__subtitle">
            Choose which marketplace cards appear in the Service Quick Links row on the home page and set their
            left-to-right order.
          </p>
        </header>
        <div className="sa-sql-page-head__actions">
          <Link
            to="/home"
            className="mca-btn mca-btn--outline sa-sql-preview-btn"
            target="_blank"
            rel="noopener noreferrer"
          >
            <FaEye aria-hidden /> Preview on Home Page
          </Link>
          <button
            type="button"
            className="mca-btn mca-btn--primary sa-sql-save-btn"
            onClick={() => void saveCards()}
            disabled={loading || saving || !section}
          >
            <FaSave aria-hidden /> {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>

      {loading ? (
        <p className="sa-placeholder-copy">Loading cards…</p>
      ) : !section ? (
        <p className="sa-placeholder-copy">
          Service Quick Links section is missing. Restore it from{" "}
          <Link to="/super-admin/master-data/home-section-management">Home Section Management</Link>.
        </p>
      ) : (
        <>
          <section className="sa-sql-order-card">
            <h2 className="sa-sql-order-card__title">Current order (left to right)</h2>
            <div className="sa-sql-order-flow">
              {moduleTileKeys.map((key, index) => (
                <React.Fragment key={key}>
                  <div className="sa-sql-order-flow__item">
                    <span className="sa-sql-order-flow__num">{index + 1}</span>
                    <span className="sa-sql-order-flow__label">{labelByKey[key] || key}</span>
                  </div>
                  {index < moduleTileKeys.length - 1 ? (
                    <FaArrowRight className="sa-sql-order-flow__arrow" aria-hidden />
                  ) : null}
                </React.Fragment>
              ))}
            </div>
          </section>

          <section className="sa-sql-card">
            <div className="sa-sql-card__head">
              <h2 className="sa-sql-card__title">Marketplace cards ({moduleTileKeys.length} visible)</h2>
              <label className="sa-sql-select-all">
                <input
                  type="checkbox"
                  checked={allVisible}
                  disabled={saving || allVisible}
                  onChange={(e) => selectAllTiles(e.target.checked)}
                />
                <span>Select All</span>
              </label>
            </div>

            <ul className="sa-sql-tile-list">
              {orderedTileOptions.map((option) => {
                const isOn = moduleTileKeys.includes(option.key);
                const index = moduleTileKeys.indexOf(option.key);
                const canMove = isOn && moduleTileKeys.length > 1;
                const Icon = TILE_ICONS[option.key] || FaTh;

                return (
                  <li
                    key={option.key}
                    className={`sa-sql-tile-list__item${isOn ? "" : " sa-sql-tile-list__item--off"}`}
                  >
                    <span className="sa-sql-tile-list__grip" aria-hidden>
                      <FaGripVertical />
                    </span>
                    <span
                      className={`sa-sql-tile-list__order${isOn ? "" : " sa-sql-tile-list__order--off"}`}
                    >
                      {isOn ? index + 1 : "—"}
                    </span>
                    <span className={`sa-sql-tile-list__icon sa-sql-tile-list__icon--${option.tone}`}>
                      <Icon aria-hidden />
                    </span>
                    <div className="sa-sql-tile-list__copy">
                      <span className="sa-sql-tile-list__title">{option.label}</span>
                      <span className="sa-sql-tile-list__desc">{option.description}</span>
                    </div>
                    <div className="sa-sql-tile-list__controls">
                      <ToggleSwitch
                        checked={isOn}
                        disabled={saving || (isOn && moduleTileKeys.length <= 1)}
                        onChange={(checked) => toggleTile(option.key, checked)}
                        label={`${isOn ? "Hide" : "Show"} ${option.label} on home`}
                      />
                      {isOn ? (
                        <div className="sa-sql-tile-list__order-btns">
                          <button
                            type="button"
                            className="sa-sql-order-btn"
                            disabled={!canMove || saving || index === 0}
                            onClick={() => moveTile(index, "up")}
                            aria-label={`Move ${option.label} up`}
                          >
                            <FaArrowUp aria-hidden />
                          </button>
                          <button
                            type="button"
                            className="sa-sql-order-btn"
                            disabled={!canMove || saving || index === moduleTileKeys.length - 1}
                            onClick={() => moveTile(index, "down")}
                            aria-label={`Move ${option.label} down`}
                          >
                            <FaArrowDown aria-hidden />
                          </button>
                        </div>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>

          <section className="sa-sql-info-grid">
            <div className="sa-sql-info-card">
              <h3>Show / Hide</h3>
              <p>
                Use the on/off switch to show or hide a card on the home page. At least one card must stay on.
              </p>
            </div>
            <div className="sa-sql-info-card">
              <h3>Change Order</h3>
              <p>
                Drag and drop or use the up/down arrows to change the left-to-right order on the home page.
              </p>
            </div>
            <div className="sa-sql-info-card">
              <h3>Hide Entire Row</h3>
              <p>
                To hide the entire Service Quick Links row, use{" "}
                <Link to="/super-admin/master-data/home-section-management">Home Section Management</Link>.
              </p>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
